import React from "react";
import cloneDeep from "lodash/cloneDeep";
import styled from "styled-components";
import { Trans } from "react-i18next";
import i18n from "i18n";
import { Select, SourceCodeEditor, TimezoneSelect, URLAllowListInput } from "components/common";
import { Button, Checkbox, Col, ControlLabel, FormGroup, HelpBlock, Input, Row } from "components/bootstrap";
import * as FormsUtils from "util/FormsUtils";
import DocsHelper from "util/DocsHelper";
import DocumentationLink from "components/support/DocumentationLink";
import { DEFAULT_JSON_TEMPLATE, DEFAULT_FORM_PARAM_TEMPLATE, DEFAULT_PLAIN_TEXT_TEMPLATE } from "./templates";
const StyledButton = styled(Button)`
  clear: both;
  display: block;
  margin-bottom: 15px;
`;
const t = (key) => i18n.t(key, { ns: "event_notifications" });
const AlertsDocsLink = ({ children = void 0 }) => /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.ALERTS, text: children });
const shouldPopulateTemplate = (currentType, currentBody) => {
  if (currentBody === "") {
    return true;
  }
  if (currentType === "JSON") {
    return currentBody === DEFAULT_JSON_TEMPLATE;
  }
  if (currentType === "FORM_DATA") {
    return currentBody === DEFAULT_FORM_PARAM_TEMPLATE;
  }
  return currentBody === DEFAULT_PLAIN_TEXT_TEMPLATE;
};
class HttpNotificationFormV2 extends React.Component {
  constructor(props) {
    super(props);
    this.propagateChange = (key, value) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      nextConfig[key] = value;
      onChange(nextConfig);
    };
    this.handleChange = (event) => {
      const { name } = event.target;
      const inputValue = FormsUtils.getValueFromInput(event.target);
      this.propagateChange(name, inputValue);
    };
    this.handleUrlChange = (event) => {
      this.propagateChange("url", event.target.value);
    };
    this.handleTimeZoneChange = (nextValue) => {
      this.propagateChange("time_zone", nextValue);
    };
    this.handleJsonBodyTemplateChange = (nextValue) => {
      this.propagateChange("body_template", nextValue);
    };
    this.handleMethodChange = (nextValue) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      nextConfig.method = nextValue;
      if (nextValue === "GET") {
        nextConfig.body_template = null;
        nextConfig.content_type = null;
      } else if (config.method === "GET") {
        nextConfig.content_type = "JSON";
        nextConfig.body_template = "";
      }
      onChange(nextConfig);
    };
    this.handleContentTypeChange = (nextValue) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      nextConfig.content_type = nextValue;
      if (shouldPopulateTemplate(config.content_type, config.body_template)) {
        if (nextValue === "JSON") {
          nextConfig.body_template = DEFAULT_JSON_TEMPLATE;
        } else if (nextValue === "FORM_DATA") {
          nextConfig.body_template = DEFAULT_FORM_PARAM_TEMPLATE;
        } else {
          nextConfig.body_template = DEFAULT_PLAIN_TEXT_TEMPLATE;
        }
      }
      onChange(nextConfig);
    };
    this.handleSecretInputChange = (event) => {
      const { name } = event.target;
      const inputValue = String(FormsUtils.getValueFromInput(event.target));
      const value = inputValue.length === 0 ? { delete_value: true } : { set_value: inputValue };
      this.setState({ [name]: inputValue });
      this.propagateChange(name, value);
    };
    this.onValidationChange = (validationState) => {
      const { setIsSubmitEnabled } = this.props;
      setIsSubmitEnabled(validationState !== "error");
    };
    this.resetSecret = (attribute) => {
      const { reset } = this.state;
      reset[attribute] = true;
      this.setState({ reset });
      this.propagateChange(attribute, { delete_value: true });
      this.setState({ [attribute]: "" });
    };
    this.undoResetSecret = (attribute) => {
      const { reset } = this.state;
      reset[attribute] = false;
      this.setState({ reset });
      this.propagateChange(attribute, { keep_value: true });
      this.setState({ [attribute]: "******" });
    };
    this.state = {
      api_secret: "",
      basic_auth: "",
      reset: {
        api_secret: false,
        basic_auth: false
      }
    };
  }
  componentDidMount() {
    var _a, _b;
    const { config, onChange } = this.props;
    const nextConfig = cloneDeep(config);
    nextConfig.basic_auth = ((_a = config.basic_auth) == null ? void 0 : _a.is_set) ? { keep_value: true } : null;
    nextConfig.api_secret = ((_b = config.api_secret) == null ? void 0 : _b.is_set) ? { keep_value: true } : null;
    onChange(nextConfig);
    this.setState({ basic_auth: config.basic_auth.is_set ? "******" : "" });
    this.setState({ api_secret: config.api_secret.is_set ? "******" : "" });
  }
  render() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
    const { config, validation } = this.props;
    const { api_secret, basic_auth } = config;
    const { reset } = this.state;
    const httpMethods = [
      { value: "POST", label: "POST" },
      { value: "GET", label: "GET" },
      { value: "PUT", label: "PUT" }
    ];
    const contentTypes = [
      { value: "JSON", label: "application/json" },
      { value: "FORM_DATA", label: "application/x-www-form-urlencoded" },
      { value: "PLAIN_TEXT", label: "text/plain" }
    ];
    const helpElement = /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { ns: "event_notifications", i18nKey: "http_v2.body_help", components: { docsLink: /* @__PURE__ */ React.createElement(AlertsDocsLink, null) } }));
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      URLAllowListInput,
      {
        label: t("http.url"),
        onChange: this.handleUrlChange,
        validationState: validation.errors.url ? "error" : null,
        validationMessage: (_c = (_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.url) == null ? void 0 : _b[0]) != null ? _c : t("http_v2.url_help"),
        onValidationChange: this.onValidationChange,
        url: config.url,
        autofocus: false
      }
    ), /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        id: "skip_tls_verification",
        name: "skip_tls_verification",
        onChange: this.handleChange,
        checked: config.skip_tls_verification
      },
      t("http.skip_tls_verification")
    ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, (basic_auth == null ? void 0 : basic_auth.keep_value) ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("http.basic_auth")), /* @__PURE__ */ React.createElement(
      StyledButton,
      {
        bsStyle: "default",
        type: "button",
        onClick: () => {
          this.resetSecret("basic_auth");
        }
      },
      t("http.reset_secret")
    )) : /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "basicAuth",
        label: /* @__PURE__ */ React.createElement("span", null, t("http.basic_auth"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("form.optional"))),
        name: "basic_auth",
        type: "password",
        onChange: this.handleSecretInputChange,
        value: this.state.basic_auth || "",
        help: t("http_v2.basic_auth_help"),
        buttonAfter: reset.basic_auth ? /* @__PURE__ */ React.createElement(
          Button,
          {
            type: "button",
            onClick: () => {
              this.undoResetSecret("basic_auth");
            }
          },
          t("http.undo_reset")
        ) : void 0
      }
    ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "api_key",
        name: "api_key",
        label: /* @__PURE__ */ React.createElement("span", null, t("http.api_key"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("form.optional"))),
        type: "text",
        onChange: this.handleChange,
        bsStyle: validation.errors.api_key ? "error" : null,
        help: (_e = (_d = validation.errors.api_key) == null ? void 0 : _d[0]) != null ? _e : t("http_v2.api_key_help"),
        value: config.api_key
      }
    ), /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        id: "api_key_as_header",
        name: "api_key_as_header",
        onChange: this.handleChange,
        checked: config.api_key_as_header
      },
      t("http.api_key_as_header")
    )), /* @__PURE__ */ React.createElement(Col, { md: 6 }, (api_secret == null ? void 0 : api_secret.keep_value) ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("http.api_secret")), /* @__PURE__ */ React.createElement(
      StyledButton,
      {
        bsStyle: "default",
        type: "button",
        onClick: () => {
          this.resetSecret("api_secret");
        }
      },
      t("http.reset_secret")
    )) : /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "apiSecret",
        label: /* @__PURE__ */ React.createElement("span", null, t("http.api_secret"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("form.optional"))),
        name: "api_secret",
        type: "password",
        onChange: this.handleSecretInputChange,
        bsStyle: validation.errors.api_secret ? "error" : null,
        help: (_g = (_f = validation.errors.api_secret) == null ? void 0 : _f[0]) != null ? _g : t("http_v2.api_secret_help"),
        value: this.state.api_secret || "",
        buttonAfter: reset.api_secret ? /* @__PURE__ */ React.createElement(
          Button,
          {
            type: "button",
            onClick: () => {
              this.undoResetSecret("api_secret");
            }
          },
          t("http.undo_reset")
        ) : void 0
      }
    ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "headers",
        name: "headers",
        label: /* @__PURE__ */ React.createElement("span", null, t("http_v2.headers"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("form.optional"))),
        type: "text",
        onChange: this.handleChange,
        bsStyle: validation.errors.headers ? "error" : null,
        help: (_i = (_h = validation.errors.headers) == null ? void 0 : _h[0]) != null ? _i : t("http_v2.headers_help"),
        value: config.headers
      }
    ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(Input, { help: t("http_v2.method_help"), id: "notification-method", label: t("http_v2.method") }, /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "method",
        name: "method",
        clearable: false,
        options: httpMethods,
        onChange: this.handleMethodChange,
        value: config.method
      }
    ))), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(
      Input,
      {
        help: t("http_v2.content_type_help"),
        id: "notification-content-type",
        label: t("http_v2.content_type")
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          id: "content-type",
          name: "content-type",
          options: contentTypes,
          disabled: config.method === "GET",
          onChange: this.handleContentTypeChange,
          clearable: false,
          value: config.content_type
        }
      )
    )), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(Input, { id: "notification-time-zone", help: t("http_v2.time_zone_help"), label: t("http_v2.time_zone") }, /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        className: "timezone-select",
        name: "time_zone",
        disabled: config.method === "GET",
        value: config.time_zone,
        clearable: false,
        onChange: this.handleTimeZoneChange
      }
    )))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, config.method !== "GET" && /* @__PURE__ */ React.createElement(
      FormGroup,
      {
        controlId: "notification-body-template",
        validationState: validation.errors.body_template ? "error" : null
      },
      /* @__PURE__ */ React.createElement(ControlLabel, null, t("http_v2.body_template")),
      /* @__PURE__ */ React.createElement(
        SourceCodeEditor,
        {
          id: "notification-body-template",
          mode: "text",
          theme: "light",
          value: config.body_template || "",
          wrapEnabled: true,
          onChange: this.handleJsonBodyTemplateChange
        }
      ),
      /* @__PURE__ */ React.createElement(HelpBlock, null, (_k = (_j = validation.errors.body_template) == null ? void 0 : _j[0]) != null ? _k : helpElement)
    ))));
  }
}
HttpNotificationFormV2.defaultConfig = {
  url: "",
  api_key_as_header: false,
  api_key: "",
  api_secret: { keep_value: true },
  basic_auth: { keep_value: true },
  skip_tls_verification: false,
  method: "POST",
  time_zone: "UTC",
  body_template: DEFAULT_JSON_TEMPLATE,
  content_type: "JSON",
  headers: ""
};
export default HttpNotificationFormV2;
